/**
 * The bills view's single date scope: one billed-date window, either a whole
 * UTC calendar month the server offered or an arbitrary custom range.
 */
export type BillDateSelection =
  | { kind: "month"; monthStart: Date }
  | { kind: "custom"; start: Date; end: Date };

export const BILL_DATE_PARAM = "billDate";

/** What the Bills page shows with no date in the URL: the last 60 days. */
export const DEFAULT_BILL_DAYS = 60;

const MONTH_PATTERN = /^(\d{4})-(\d{2})$/;
const RANGE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})\.\.(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86_400_000;

function pad(value: number, length: number): string {
  return value.toString().padStart(length, "0");
}

// Date.UTC silently rolls overflow over (month 13 → next January), so reject
// anything that doesn't survive the round trip.
function utcDayStart(year: number, month: number, day: number): Date | null {
  const date = new Date(Date.UTC(year, month - 1, day));
  const rolled =
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day;
  return rolled ? null : date;
}

/** Inclusive last instant of a UTC day, matching the server's window bounds. */
function utcDayEnd(year: number, month: number, day: number): Date | null {
  const start = utcDayStart(year, month, day);
  return start ? new Date(start.getTime() + DAY_MS - 1) : null;
}

function utcDayString(date: Date): string {
  return `${pad(date.getUTCFullYear(), 4)}-${pad(date.getUTCMonth() + 1, 2)}-${pad(date.getUTCDate(), 2)}`;
}

/**
 * Parse the URL's date scope. Returns null for absent or malformed values so a
 * hand-edited link falls back to the default (last 60 days) instead of erroring.
 */
export function parseBillDateSelection(
  raw: string | null,
): BillDateSelection | null {
  if (!raw) {
    return null;
  }

  const month = MONTH_PATTERN.exec(raw);
  if (month) {
    const monthStart = utcDayStart(Number(month[1]), Number(month[2]), 1);
    return monthStart ? { kind: "month", monthStart } : null;
  }

  const range = RANGE_PATTERN.exec(raw);
  if (!range) {
    return null;
  }
  const start = utcDayStart(
    Number(range[1]),
    Number(range[2]),
    Number(range[3]),
  );
  const end = utcDayEnd(Number(range[4]), Number(range[5]), Number(range[6]));
  if (!start || !end || start.getTime() > end.getTime()) {
    return null;
  }
  return { kind: "custom", start, end };
}

/**
 * The billed-date window a selection scopes to, on the same UTC month axis the
 * server buckets on — so the table can scope itself without waiting on the RPC.
 */
export function windowFromBillDateSelection(
  selection: BillDateSelection | null,
): { start: Date; end: Date } | null {
  if (!selection) {
    return null;
  }
  if (selection.kind === "custom") {
    return { start: selection.start, end: selection.end };
  }
  const { monthStart } = selection;
  const nextMonth = Date.UTC(
    monthStart.getUTCFullYear(),
    monthStart.getUTCMonth() + 1,
    1,
  );
  return { start: monthStart, end: new Date(nextMonth - 1) };
}

/**
 * The window covering `now`'s month and the `months - 1` before it, on the same
 * UTC axis the server buckets months on.
 */
export function lastMonthsSelection(
  now: Date,
  months: number,
): BillDateSelection {
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  return {
    kind: "custom",
    start: new Date(Date.UTC(year, month - (months - 1), 1)),
    end: new Date(Date.UTC(year, month + 1, 1) - 1),
  };
}

/**
 * The window covering `now`'s UTC day and the `days - 1` before it, through the
 * last instant of today so a bill charged later today is still in scope.
 */
export function lastDaysSelection(now: Date, days: number): BillDateSelection {
  const today = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );
  return {
    kind: "custom",
    start: new Date(today - (days - 1) * DAY_MS),
    end: new Date(today + DAY_MS - 1),
  };
}

export function serializeBillDateSelection(
  selection: BillDateSelection,
): string {
  if (selection.kind === "month") {
    const { monthStart } = selection;
    return `${pad(monthStart.getUTCFullYear(), 4)}-${pad(monthStart.getUTCMonth() + 1, 2)}`;
  }
  return `${utcDayString(selection.start)}..${utcDayString(selection.end)}`;
}

// Custom ranges scope on the same UTC axis the server buckets months on, so a
// range never clips a month edge the way a tz-resolved one would.
export function customSelectionFromPlainDates([start, end]: [
  Temporal.PlainDate,
  Temporal.PlainDate,
]): BillDateSelection {
  return {
    kind: "custom",
    start: new Date(Date.UTC(start.year, start.month - 1, start.day)),
    end: new Date(Date.UTC(end.year, end.month - 1, end.day) + DAY_MS - 1),
  };
}

function toPlainDate(date: Date): Temporal.PlainDate {
  return Temporal.PlainDate.from({
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  });
}

export function plainDatesFromCustomSelection(
  selection: BillDateSelection | null,
): [Temporal.PlainDate, Temporal.PlainDate] | undefined {
  if (selection?.kind !== "custom") {
    return undefined;
  }
  return [toPlainDate(selection.start), toPlainDate(selection.end)];
}
